import { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Decimal } from 'decimal.js'
import NumberFormat from 'react-number-format'

import { CRYPTO_REDEMPTION_SCREEN_KEYS } from 'models/Redemptions/constants'

import TextInput from 'components/TextInput'

import { STRING, EVENT_TYPE } from 'resources'
import { reklaimPtsIcon } from 'resources/images'

import formatCrypto from 'utils/format-crypto'
import formatCurrency from 'utils/format-currency'
import { sendAnalyticsEvent } from 'services/analytics'

import { CRYPTO_CONFIGS } from 'screens/Wallet/WalletRedemptions/CryptoRedemption/constants'

import {
  Form,
  ContinueButton,
  PointsContainer,
  PointsConversionCardTitle,
  PointsConversionTitleSection,
  Icon,
  ButtonContainer,
  ConvertedPointsContainer,
  ApproximateText,
} from '../styles'

const PointsConversionScreen = ({
  crypto,
  currency,
  points,
  setPoints,
  pointsInCrypto,
  approximateValue,
  minPoints,
  maxPoints,
  goToNextScreen,
}) => {
  const formattedPointsInCrypto = useMemo(() => formatCrypto(pointsInCrypto, crypto), [pointsInCrypto, crypto])
  const formattedApproximateValue = useMemo(() => formatCurrency(approximateValue), [approximateValue])

  const isGreaterThanBalance = useMemo(() => maxPoints.lt(points), [points, maxPoints])
  const isLessThanMinimumBalance = useMemo(() => minPoints.gt(points), [points, minPoints])
  const isInvalid = isGreaterThanBalance || isLessThanMinimumBalance

  useEffect(() => {
    sendAnalyticsEvent(EVENT_TYPE.redeemCryptoAmountScreenView, { crypto })
  }, [crypto])

  const handleSubmit = () => {
    if (isInvalid) {
      return
    }
    sendAnalyticsEvent(EVENT_TYPE.redeemCryptoAmountScreenContinueClick, { crypto })
    goToNextScreen(CRYPTO_REDEMPTION_SCREEN_KEYS.walletAddressEnteringScreen)
  }

  const handleChange = useCallback(
    (value) => {
      if (!value || value >= 0) {
        const valueToNumber = parseInt(value, 10)
        setPoints(valueToNumber || 0)
      }
    },
    [setPoints],
  )

  let errorMessage
  if (isLessThanMinimumBalance) {
    errorMessage = STRING.cryptoRedeemScreen.pointsConversionScreen.minPointsAmountErrorMessage(minPoints)
  } else if (isGreaterThanBalance) {
    errorMessage = STRING.cryptoRedeemScreen.pointsConversionScreen.pointsGreaterThanBalance
  }

  return (
    <Form onSubmit={handleSubmit}>
      <PointsContainer>
        <PointsConversionTitleSection>
          <Icon src={reklaimPtsIcon} />
          <PointsConversionCardTitle>
            {STRING.cryptoRedeemScreen.pointsConversionScreen.ptsToRedeem}
          </PointsConversionCardTitle>
        </PointsConversionTitleSection>
        <TextInput
          tag={NumberFormat}
          invalid={isLessThanMinimumBalance || isGreaterThanBalance}
          thousandSeparator
          suffix={STRING.cryptoRedeemScreen.pointsConversionScreen.pointsSuffix}
          value={points.toString()}
          placeholder={STRING.cryptoRedeemScreen.pointsConversionScreen.pointsPlaceholder}
          onValueChange={({ value }) => handleChange(value)}
          errorMessage={errorMessage}
          allowNegative={false}
        />
        <PointsConversionTitleSection>
          <Icon src={CRYPTO_CONFIGS[crypto]?.icon} />
          <PointsConversionCardTitle>
            {STRING.cryptoRedeemScreen.pointsConversionScreen.cryptoToReceive(STRING.crypto[crypto].ticker)}
          </PointsConversionCardTitle>
        </PointsConversionTitleSection>
        <ConvertedPointsContainer>
          <span>{formattedPointsInCrypto}</span>
          <ApproximateText>
            {STRING.cryptoRedeemScreen.pointsConversionScreen.approximateText(formattedApproximateValue, currency)}
          </ApproximateText>
        </ConvertedPointsContainer>
      </PointsContainer>
      <ButtonContainer>
        <ContinueButton onClick={handleSubmit} disabled={isInvalid} withArrow>
          {STRING.cryptoRedeemScreen.pointsConversionScreen.continueButton}
        </ContinueButton>
      </ButtonContainer>
    </Form>
  )
}

PointsConversionScreen.propTypes = {
  crypto: PropTypes.oneOf(Object.keys(CRYPTO_CONFIGS)).isRequired,
  currency: PropTypes.string.isRequired,
  points: PropTypes.number.isRequired,
  setPoints: PropTypes.func.isRequired,
  pointsInCrypto: PropTypes.instanceOf(Decimal).isRequired,
  approximateValue: PropTypes.instanceOf(Decimal).isRequired,
  maxPoints: PropTypes.instanceOf(Decimal).isRequired,
  minPoints: PropTypes.instanceOf(Decimal).isRequired,
  goToNextScreen: PropTypes.func.isRequired,
}

export default PointsConversionScreen
